/**
 * Everything the IFC attribute feature knows: one entry per registered bim
 * tileset (sidecar index + status), per-tileset visibility state, the single
 * selection and the panel state. TilesLoader registers tilesets; the pick
 * handler, the panel and Layer Control read and update this store.
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import type { Cesium3DTileset } from 'cesium';
import { toast } from 'sonner';
import { buildIndex, type IfcElement, type IfcIndex } from '@/helper/ifc/index';
import { fetchSidecar } from '@/helper/ifc/sidecar';
import {
	buildTree,
	DEFAULT_GROUP_KEY,
	initialVisibility,
	resolveHidden,
	type IfcTree,
	type IfcVisibilityState,
} from '@/helper/ifc/tree';
import { IfcVisibilityApplier } from '@/helper/ifc/visibilityApplier';
import { flyToBbox } from '@/helper/ifc/zoom';
import { t } from '@/i18n';
import { useCesium } from './CesiumContext';

export type IfcIndexStatus = 'loading' | 'ready' | 'error';

export type IfcTilesetEntry = {
	key: string;
	name: string;
	tileset: Cesium3DTileset;
	status: IfcIndexStatus;
	index: IfcIndex | null;
};

/** `summary` is what the tile itself knows (IfcClass, Name, GlobalId), captured at pick time. */
export type IfcSelection = {
	key: string;
	featureId: number;
	summary: Record<string, string>;
} | null;

export type IfcPanelTab = 'structure' | 'properties';
export type IfcPanelState = { key: string; tab: IfcPanelTab } | null;

export type IfcRegistration = {
	key: string;
	name: string;
	tileset: Cesium3DTileset;
	sidecarUrl: string | null;
};

interface IfcFeatureContextType {
	entries: Record<string, IfcTilesetEntry>;
	visibility: Record<string, IfcVisibilityState>;
	trees: Record<string, IfcTree>;
	selection: IfcSelection;
	selectedElement: IfcElement | null;
	panel: IfcPanelState;
	registerIfcTileset: (registration: IfcRegistration) => void;
	unregisterIfcTileset: (key: string) => void;
	select: (selection: IfcSelection) => void;
	clearSelection: () => void;
	openPanel: (key: string, tab: IfcPanelTab) => void;
	closePanel: () => void;
	setPanelTab: (tab: IfcPanelTab) => void;
	updateVisibility: (
		key: string,
		update: (state: IfcVisibilityState) => IfcVisibilityState
	) => void;
	zoomTo: (key: string, bbox: number[] | null) => void;
}

const IfcFeatureContext = createContext<IfcFeatureContextType | null>(null);

export function IfcFeatureProvider({ children }: { children: ReactNode }) {
	const { viewer } = useCesium();
	const [entries, setEntries] = useState<Record<string, IfcTilesetEntry>>({});
	const [visibility, setVisibility] = useState<Record<string, IfcVisibilityState>>({});
	const [selection, setSelection] = useState<IfcSelection>(null);
	const [panel, setPanel] = useState<IfcPanelState>(null);

	const appliers = useRef(new Map<string, IfcVisibilityApplier>());
	const aborters = useRef(new Map<string, AbortController>());
	const viewerRef = useRef(viewer);
	viewerRef.current = viewer;
	const entriesRef = useRef(entries);
	entriesRef.current = entries;

	const requestRender = useCallback(() => {
		const current = viewerRef.current;
		if (current && !current.isDestroyed()) current.scene.requestRender();
	}, []);

	const unregisterIfcTileset = useCallback((key: string) => {
		aborters.current.get(key)?.abort();
		aborters.current.delete(key);
		appliers.current.get(key)?.destroy();
		appliers.current.delete(key);
		setEntries((prev) => {
			if (!(key in prev)) return prev;
			const next = { ...prev };
			delete next[key];
			return next;
		});
		setVisibility((prev) => {
			if (!(key in prev)) return prev;
			const next = { ...prev };
			delete next[key];
			return next;
		});
		setSelection((prev) => (prev?.key === key ? null : prev));
		setPanel((prev) => (prev?.key === key ? null : prev));
	}, []);

	const registerIfcTileset = useCallback(
		(registration: IfcRegistration) => {
			const { key, name, tileset, sidecarUrl } = registration;
			unregisterIfcTileset(key);
			appliers.current.set(key, new IfcVisibilityApplier(tileset, requestRender));
			setEntries((prev) => ({
				...prev,
				[key]: { key, name, tileset, status: sidecarUrl ? 'loading' : 'error', index: null },
			}));
			setVisibility((prev) => ({ ...prev, [key]: initialVisibility() }));
			if (!sidecarUrl) return;

			const controller = new AbortController();
			aborters.current.set(key, controller);
			fetchSidecar(sidecarUrl, controller.signal)
				.then((sidecar) => {
					const index = buildIndex(sidecar);
					setEntries((prev) =>
						prev[key] ? { ...prev, [key]: { ...prev[key], status: 'ready', index } } : prev
					);
				})
				.catch((error: unknown) => {
					if (controller.signal.aborted) return;
					console.warn('IFC sidecar unavailable:', key, error);
					toast.error(t('viewer.ifc.structure.loadFailed', { name: name || t('viewer.ifc.ifcModel') }));
					setEntries((prev) =>
						prev[key] ? { ...prev, [key]: { ...prev[key], status: 'error' } } : prev
					);
				})
				.finally(() => {
					if (aborters.current.get(key) === controller) aborters.current.delete(key);
				});
		},
		[requestRender, unregisterIfcTileset]
	);

	// Rebuild a tree only when its index or its group key changes.
	const groupKeySignature = Object.entries(visibility)
		.map(([key, state]) => `${key}=${state.groupKey}`)
		.join('|');
	const trees = useMemo(() => {
		const out: Record<string, IfcTree> = {};
		for (const entry of Object.values(entries)) {
			if (!entry.index) continue;
			out[entry.key] = buildTree(entry.index, visibility[entry.key]?.groupKey ?? DEFAULT_GROUP_KEY);
		}
		return out;
		// visibility is covered by groupKeySignature on purpose.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entries, groupKeySignature]);

	// Push hidden set + selection into every applier.
	useEffect(() => {
		for (const entry of Object.values(entries)) {
			const applier = appliers.current.get(entry.key);
			if (!applier) continue;
			const tree = trees[entry.key];
			const state = visibility[entry.key];
			const hidden = tree && state ? resolveHidden(tree, state) : new Set<number>();
			applier.update(hidden, selection?.key === entry.key ? selection.featureId : null);
		}
	}, [entries, trees, visibility, selection]);

	// The provider sits above CesiumInitializer's `{viewer && children}` gate, so it
	// outlives the subtree that registered these tilesets. Once the viewer is gone every
	// entry describes a destroyed tileset — drop the lot rather than let a stale key
	// reach zoomTo or the panel.
	useEffect(() => {
		if (viewer) return;
		appliers.current.forEach((applier) => applier.destroy());
		appliers.current.clear();
		aborters.current.forEach((controller) => controller.abort());
		aborters.current.clear();
		setEntries({});
		setVisibility({});
		setSelection(null);
		setPanel(null);
	}, [viewer]);

	// Unmount: the viewer goes away with us, but listeners and fetches must not linger.
	useEffect(() => {
		const applierMap = appliers.current;
		const aborterMap = aborters.current;
		return () => {
			applierMap.forEach((applier) => applier.destroy());
			applierMap.clear();
			aborterMap.forEach((controller) => controller.abort());
			aborterMap.clear();
		};
	}, []);

	const select = useCallback((next: IfcSelection) => setSelection(next), []);
	const clearSelection = useCallback(() => setSelection(null), []);
	const openPanel = useCallback((key: string, tab: IfcPanelTab) => setPanel({ key, tab }), []);
	const closePanel = useCallback(() => {
		setPanel(null);
		setSelection(null);
	}, []);
	const setPanelTab = useCallback(
		(tab: IfcPanelTab) => setPanel((prev) => (prev ? { ...prev, tab } : prev)),
		[]
	);
	const updateVisibility = useCallback(
		(key: string, update: (state: IfcVisibilityState) => IfcVisibilityState) => {
			setVisibility((prev) => {
				const current = prev[key];
				if (!current) return prev;
				const next = update(current);
				return next === current ? prev : { ...prev, [key]: next };
			});
		},
		[]
	);
	const zoomTo = useCallback((key: string, bbox: number[] | null) => {
		const current = viewerRef.current;
		const entry = entriesRef.current[key];
		if (!current || !entry) return;
		flyToBbox(current, entry.tileset, bbox);
	}, []);

	const selectedElement = useMemo(() => {
		if (!selection) return null;
		return entries[selection.key]?.index?.elements[selection.featureId] ?? null;
	}, [entries, selection]);

	const value = useMemo<IfcFeatureContextType>(
		() => ({
			entries,
			visibility,
			trees,
			selection,
			selectedElement,
			panel,
			registerIfcTileset,
			unregisterIfcTileset,
			select,
			clearSelection,
			openPanel,
			closePanel,
			setPanelTab,
			updateVisibility,
			zoomTo,
		}),
		[
			entries,
			visibility,
			trees,
			selection,
			selectedElement,
			panel,
			registerIfcTileset,
			unregisterIfcTileset,
			select,
			clearSelection,
			openPanel,
			closePanel,
			setPanelTab,
			updateVisibility,
			zoomTo,
		]
	);

	return <IfcFeatureContext.Provider value={value}>{children}</IfcFeatureContext.Provider>;
}

export function useIfcFeatures(): IfcFeatureContextType {
	const context = useContext(IfcFeatureContext);
	if (!context) throw new Error('useIfcFeatures must be used within an IfcFeatureProvider');
	return context;
}
